import React from 'react';
import { Smartphone, Monitor, CheckCircle, ArrowRight, Code2, Database, Cloud, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { images } from '../data/mock';

const Solutions = () => {
  return (
    <section id="solutions" className="relative py-24 bg-slate-900 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
            <span className="text-blue-400 text-xs font-semibold uppercase tracking-wider">Our Solutions</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4 font-display">
            Complete <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Software Solutions</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            From enterprise applications to mobile solutions, we deliver end-to-end software consulting services.
          </p>
        </div>

        {/* Solutions Grid */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Enterprise Software Solution */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative bg-slate-800/50 rounded-3xl p-8 border border-slate-700/50 hover:border-blue-500/30 transition-colors">
              <div className="flex items-start gap-6 mb-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <Monitor className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-3 font-display">Enterprise Applications</h3>
                  <p className="text-slate-400 mb-4">
                    Scalable web applications and admin panels with real-time analytics and comprehensive dashboards.
                  </p>
                  <ul className="space-y-2">
                    {['Custom Web Applications', 'Admin Dashboards', 'ERP Systems', 'API Development'].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-blue-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {/* Code Image */}
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img
                  src={images.code}
                  alt="Software Development"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>
          </div>

          {/* Mobile & Cloud Solution */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative bg-slate-800/50 rounded-3xl p-8 border border-slate-700/50 hover:border-cyan-500/30 transition-colors">
              <div className="flex items-start gap-6 mb-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                    <Smartphone className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-3 font-display">Mobile & Cloud Solutions</h3>
                  <p className="text-slate-400 mb-4">
                    Native mobile apps and cloud infrastructure with DevOps automation and security.
                  </p>
                  <ul className="space-y-2">
                    {['iOS & Android Apps', 'Cloud Architecture', 'DevOps & CI/CD', 'Security Solutions'].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-cyan-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {/* Mobile Development Image */}
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img
                  src={images.mobileApp}
                  alt="Mobile App Development"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Additional Services Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
          {[
            { icon: Code2, title: 'Custom Development', desc: '.NET, Java, Python' },
            { icon: Database, title: 'Database Solutions', desc: 'SQL, NoSQL, Cloud' },
            { icon: Cloud, title: 'Cloud Services', desc: 'AWS, Azure, GCP' },
            { icon: Shield, title: 'Cybersecurity', desc: 'Audit & Compliance' }
          ].map((item, index) => (
            <div key={index} className="p-6 rounded-2xl bg-slate-800/30 border border-slate-700/50 text-center hover:border-blue-500/30 transition-colors">
              <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-4">
                <item.icon className="w-6 h-6 text-blue-400" />
              </div>
              <h4 className="text-white font-semibold mb-1">{item.title}</h4>
              <p className="text-slate-500 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-gradient text-white font-semibold px-8 py-6 text-lg rounded-xl shadow-xl shadow-blue-500/25 hover:shadow-blue-500/40 transition-all group"
          >
            Discuss Your Project
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Solutions;
